/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Page,
  PageBody,
  Result,
  Stack,
} from '@pypestream/design-system';
import { FC, useEffect } from 'react';
import {
  ErrorBoundary as ReactErrorBoundary,
  useErrorBoundary,
} from 'react-error-boundary';
import * as Sentry from '@sentry/react';
import { isDevelopment } from '@pypestream/utils';

import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import { formatErrorMessage, logError } from './helpers';
import { useErrorTag } from './hooks';
import { ErrorPageProps, ChildrenProps, ErrorTags } from './types';

export const FormattedErrorMessage: FC<{ message: string }> = ({ message }) => {
  const { msg, jsonMsg, query } = formatErrorMessage(message);

  const jsonMessage = jsonMsg ? (
    <>
      <br />
      <br />
      {jsonMsg}
    </>
  ) : null;

  const gqlQuery = query ? (
    <>
      <br />
      <br />
      {query}
    </>
  ) : null;

  return (
    <pre
      style={{
        color: 'white',
        padding: '20px',
        background: 'black',
        whiteSpace: 'pre-wrap',
      }}
    >
      {msg}
      {jsonMessage}
      {gqlQuery}
    </pre>
  );
};

const ErrorPage: FC<ErrorPageProps> = ({ error, resetErrorBoundary }) => {
  return (
    <Page background="secondary">
      <PageBody background="none">
        <Result
          align="center"
          status="error"
          label={Array.isArray(error) ? error[0].message : error.message}
        >
          <Stack gutter="xsmall" justifyContent="center" slot="footer">
            <Button
              size="large"
              variant="secondary"
              onClick={resetErrorBoundary}
            >
              Reload Page
            </Button>
            <Button
              size="large"
              onClick={() => (document.location.pathname = '/')}
            >
              Go Home
            </Button>
          </Stack>
        </Result>
      </PageBody>
    </Page>
  );
};

const UncaughtErrorBoundary: FC<ChildrenProps> = ({ children }) => {
  const { isLocalError, isGlobalError, isXstateError } = useErrorTag();
  const {
    errors: proStudioMachineErrors,
    userInfo,
    projectInfo,
  } = useProStudioCtxSelector((ctx) => ({
    errors: ctx.errors,
    userInfo: ctx.userInfo,
    projectInfo: {
      name: ctx.project?.name,
      id: ctx.project?.id,
      customerId: ctx.customerId,
      orgName: ctx.orgName,
      solutionName: ctx.botName,
    },
  }));

  const errors = !isLocalError ? proStudioMachineErrors : null;
  const { showBoundary } = useErrorBoundary();

  const handler = (e: ErrorEvent) => {
    showBoundary(e);
  };

  const promiseRejectionHandler = (e: PromiseRejectionEvent) => {
    showBoundary(
      e.reason instanceof Error ? e.reason : new Error(String(e.reason))
    );
  };

  const removeErrorListener = () => {
    if (typeof window === 'undefined') {
      return;
    }

    window.removeEventListener('error', handler);
    window.removeEventListener('unhandledrejection', promiseRejectionHandler);
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (isLocalError || isXstateError) {
      removeErrorListener();

      return;
    }

    window.addEventListener('error', handler);
    window.addEventListener('unhandledrejection', promiseRejectionHandler);

    return () => {
      removeErrorListener();
    };
  }, [isLocalError]);

  useEffect(() => {
    if (isGlobalError && errors && errors.length > 0) {
      showBoundary(errors);
    }
  }, [isGlobalError]);

  useEffect(() => {
    if (
      (isGlobalError || isLocalError || isXstateError) &&
      proStudioMachineErrors &&
      !isDevelopment
    ) {
      proStudioMachineErrors.forEach((error) => {
        Sentry.captureException(error, {
          tags: {
            ...(isXstateError && { [ErrorTags.xstate]: isXstateError }),
            ...(isLocalError && { [ErrorTags.local]: isLocalError }),
            ...(isGlobalError && { [ErrorTags.global]: isGlobalError }),
          },
          extra: {
            source: 'UncaughtErrorBoundary',
            type: isGlobalError
              ? 'GlobalError'
              : isLocalError
                ? 'LocalError'
                : isXstateError
                  ? 'XstateError'
                  : 'UnknownError',
            error,
            platformInfo: {
              userInfo,
              projectInfo,
            },
          },
        });
      });
    }
  }, [
    isGlobalError,
    isLocalError,
    isXstateError,
    proStudioMachineErrors,
    isDevelopment,
  ]);

  useEffect(() => {
    if (!isGlobalError || !errors || errors.length === 0) {
      return;
    }

    showBoundary(errors);
  }, [errors]);

  return <>{children}</>;
};

export const ErrorBoundary: FC<ChildrenProps> = ({ children }) => {
  const resetApp = () => {
    window.location.reload();
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorPage}
      onError={(e, i) => logError(e, i)}
      onReset={resetApp}
    >
      <UncaughtErrorBoundary>{children}</UncaughtErrorBoundary>
    </ReactErrorBoundary>
  );
};
